import { Injectable } from "@angular/core";

import {
  BehaviorSubject,
  catchError,
  EMPTY,
  finalize,
  Observable,
  switchMap,
} from "rxjs";
import { MultiAction } from "../model/multi-action";
import { Labeler } from "../model/labeler";
import { AuthService } from "../auth.service";
import { Messages } from "../model/messages";
import { TicketService } from "./ticket.service";
import { Ticket } from "../model/ticket";
import { ActionResult } from "../model/action-result";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class MultiActionService implements Labeler {
  public _currentAction: BehaviorSubject<MultiAction[]>;
  public selectedAction: BehaviorSubject<MultiAction | null>;
  // statusId links a status-change action to the customer-configured status in the backend.
  // Name and color for those actions are resolved via StatusService at runtime.
  // For operational actions (archive/activate/clear) there is no status equivalent;
  // their colors are configured here. See README.md § "Multi-action colors".
  private actions = [
    { id: 1, name: "action.in_progress", color: "#f59e0b", statusId: 1 },
    { id: 2, name: "action.wait",        color: "#6366f1", statusId: 2 },
    { id: 3, name: "action.done",        color: "#22c55e", statusId: 3 },
    { id: 4, name: "action.archive",     color: "#da430d" },
    { id: 5, name: "action.activate",    color: "#2e7d32" },
    { id: 6, name: "action.clear",       color: "#c92929" },
  ];

  public selectedTicketIds: number[] = [];
  public actionResults: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public processingCompleted = new BehaviorSubject<void>(undefined);

  constructor(
    private auth: AuthService,
    private ticketService: TicketService,
    private translate: TranslateService
  ) {
    this._currentAction = new BehaviorSubject<MultiAction[]>(this.actions);
    this.selectedAction = new BehaviorSubject<MultiAction | null>(null);
  }
  public actionsAll(token: string): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this._currentAction.next(this.actions);
      observer.next(Messages.EMPTY);
    });
  }
  public getActionById(token: string, actionId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      const data = this.actions.find((a) => {
        actionId == a.id;
      });
      if (!data) {
        observer.next(Messages.from({ action: ["action.unknown"] }));
        return;
      }
      this._currentAction.next([data]);
      observer.next(Messages.EMPTY);
    });
  }

  processTickets() {
    const auth = this.auth.token;
    const currentAction = this.selectedAction.value?.name?.toLowerCase();
    const selectedTickets = this.ticketService.selectedTickets.getValue();

    if (!selectedTickets || selectedTickets.length === 0) {
      return;
    }

    this.actionResults.next([]);
    let processedCount = 0;
    const totalTickets = selectedTickets.length;

    const finalizeAndEmit = () => {
      processedCount++;
      if (processedCount === totalTickets) {
        this.processingCompleted.next();

        this.ticketService.selectedTickets.next([]);
      }
    };

    for (const id of selectedTickets.map((t) => t.id)) {
      if (id === null) continue;

      this.ticketService.getTicketById(auth, id).subscribe(() => {
        const ticket = this.ticketService.currentTicket;

        const callback = () => {
          this.ticketService.toggleTicketSelection(ticket);
          finalizeAndEmit();
        };

        switch (currentAction) {
          case "action.activate":
            this.applyActionAndUpdate(
              ticket,
              auth,
              () => this.actionActive(ticket, auth),
              callback
            );
            break;

          case "action.archive":
            this.applyActionAndUpdate(
              ticket,
              auth,
              () => this.actionArchive(ticket, auth),
              callback
            );
            break;

          case "action.done":
            this.applyActionAndUpdate(
              ticket,
              auth,
              () => this.actionStatusDone(ticket, auth),
              callback
            );
            break;

          case "action.in_progress":
            this.applyActionAndUpdate(
              ticket,
              auth,
              () => this.actionStatusInProgress(ticket, auth),
              callback
            );
            break;

          case "action.wait":
            this.applyActionAndUpdate(
              ticket,
              auth,
              () => this.actionStatusWait(ticket, auth),
              callback
            );
            break;

          case "action.clear":
            if (ticket.id !== null) {
              this.ticketService
                .deleteTicket(auth, ticket.id)
                .subscribe((m) => {
                  this.actionResults.next([
                    ...this.actionResults.value,
                    new ActionResult(ticket, m),
                  ]);
                  callback();
                });
            } else {
              callback();
            }
            break;

          default:
            finalizeAndEmit();
        }
      });
    }
  }

  private actionActive(ticket: Ticket, token: string) {
    ticket.archived = false;
    this.ticketService.updateTicket(token, ticket).subscribe((m) => {
      const results = this.actionResults.value;
      results.push(new ActionResult(ticket, m));
      this.actionResults.next(results);
      this.ticketService.toggleTicketSelection(ticket);
    });
  }

  private actionArchive(ticket: Ticket, token: string) {
    ticket.archived = true;
    this.ticketService.updateTicket(token, ticket).subscribe((m) => {
      const results = this.actionResults.value;
      results.push(new ActionResult(ticket, m));
      this.actionResults.next(results);

      this.ticketService.toggleTicketSelection(ticket);
    });
  }

  private actionStatusDone(ticket: Ticket, token: string) {
    ticket.statusId = 3;
    this.ticketService.updateTicket(token, ticket).subscribe((m) => {
      const results = this.actionResults.value;
      results.push(new ActionResult(ticket, m));
      this.actionResults.next(results);

      this.ticketService.toggleTicketSelection(ticket);
    });
  }
  private actionStatusInProgress(ticket: Ticket, token: string) {
    ticket.statusId = 1;
    this.ticketService.updateTicket(token, ticket).subscribe((m) => {
      const results = this.actionResults.value;
      results.push(new ActionResult(ticket, m));
      this.actionResults.next(results);

      this.ticketService.toggleTicketSelection(ticket);
    });
  }
  private actionStatusWait(ticket: Ticket, token: string) {
    ticket.statusId = 2;
    this.ticketService.updateTicket(token, ticket).subscribe((m) => {
      const results = this.actionResults.value;
      results.push(new ActionResult(ticket, m));
      this.actionResults.next(results);

      this.ticketService.toggleTicketSelection(ticket);
    });
  }
  private actionClear(ticket: Ticket, token: string) {
    if (ticket.id !== null) {
      this.ticketService.deleteTicket(token, ticket.id).subscribe((m) => {
        const results = this.actionResults.value;
        results.push(new ActionResult(ticket, m));
        this.actionResults.next(results);

        this.ticketService.toggleTicketSelection(ticket);
      });
    }
  }
  private applyActionAndUpdate(
    ticket: Ticket,
    token: string,
    actionFn: () => void,
    finalize: () => void
  ) {
    actionFn();
  }

  public select(action: MultiAction) {
    this.selectedAction.next(action);
  }

  public get selectedActionObservable(): Observable<MultiAction | null> {
    return this.selectedAction.asObservable();
  }

  public get actionObservable(): Observable<MultiAction[]> {
    return this._currentAction;
  }

  public get actionData(): MultiAction[] {
    return this._currentAction.value;
  }

  public get currentAction(): MultiAction {
    const value = this.selectedAction.value;
    return value || MultiAction.EMPTY;
  }

  public labelForId(id: number): string {
    let label = "-";
    if (id && id > 0) {
      const found = this.actions.find((action) => id === action.id);
      if (found) {
        label = found.name;
      }
    }
    return label;
  }
  public makeLabel(id: number): Observable<string> {
    const observable = new BehaviorSubject("");
    const label = this.labelForId(id);
    observable.next(label);
    return observable;
  }
  getIdByName(name: string): number | null {
    const actions = this.actions.find((actions) => actions.name === name);
    return actions ? actions.id : null;
  }
  public colorForId(id: number): string {
    return this.actions.find((a) => a.id === id)?.color ?? "#888888";
  }
  public statusIdForActionId(actionId: number): number | null {
    return this.actions.find((a) => a.id === actionId)?.statusId ?? null;
  }
  public makeColor(id: number): Observable<string> {
    return new BehaviorSubject(this.colorForId(id));
  }
  public makeBackgroundColor(id: number): Observable<string> {
    return new BehaviorSubject(this.colorForId(id));
  }
  public makeImportance(id: number): Observable<number> {
    return new BehaviorSubject(1);
  }
}
